/**
 * VIN Pulse OS Web - Login Page
 * Clean, minimal authentication page
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logoImage from '../assets/logoVin.jpg';
import styles from './LoginPage.module.css';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [forgotMessage, setForgotMessage] = useState<string | null>(null);
  const { login, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      alert('Please enter email and password');
      return;
    }

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      // Error is handled by context and displayed in error state
    }
  };

  return (
    <div className={styles.loginContainer}>
      {/* Logo and Header - Outside Card */}
      <div className={styles.logoSection}>
        <img src={logoImage} alt="VIN Pulse OS" className={styles.logo} />
        <h1 className={styles.appName}>VIN Pulse OS</h1>
        <p className={styles.appSubtitle}>Distribution Management Platform</p>
      </div>

      {/* Card with Form Inside */}
      <div className={styles.loginCard}>
        {/* Welcome Section - Inside Card */}
        <div className={styles.welcomeSection}>
          <h2 className={styles.welcomeTitle}>Welcome Back!</h2>
          <p className={styles.welcomeSubtitle}>Sign in to access your dashboard</p>
        </div>

        {/* Form */}
        <form onSubmit={handleLogin} className={styles.loginForm}>
          {/* Error Message */}
          {error && (
            <div className={styles.errorContainer}>
              <span className={styles.errorIcon}>⚠️</span>
              <p className={styles.errorMessage}>{error}</p>
            </div>
          )}

          {/* Email Input */}
          <div className={styles.formGroup}>
            <label htmlFor="email" className={styles.formLabel}>
              Email Address
            </label>
            <input
              id="email"
              type="email"
              className={styles.formInput}
              placeholder="john.smith@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              autoFocus
              autoComplete="email"
            />
          </div>

          {/* Password Input */}
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.formLabel}>
              Password
            </label>
            <div className={styles.passwordInputContainer}>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={styles.formInput}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
              />
              <button
                type="button"
                className={styles.togglePasswordBtn}
                onClick={() => setShowPassword(!showPassword)}
                disabled={isLoading}
                aria-label="Toggle password visibility"
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className={styles.forgotPasswordContainer}>
            <button
              type="button"
              className={styles.forgotPasswordBtn}
              onClick={() => setForgotMessage('Contact your administrator to reset your password. Self-service password reset is not yet enabled.')}
            >
              Forgot Password?
            </button>
          </div>
          {forgotMessage && (
            <p style={{ color: '#6B7280', fontSize: 13, margin: '4px 0 12px', textAlign: 'center' }}>
              {forgotMessage}
            </p>
          )}

          {/* Continue Button */}
          <button
            type="submit"
            className={styles.continueButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner}></span>
                Signing in...
              </>
            ) : (
              <>
                Continue <span className={styles.arrow}>→</span>
              </>
            )}
          </button>

          {/* Register CTA */}
          <div className={styles.signupCta}>
            <span className={styles.signupText}>New to VIN Pulse OS?</span>
            <button
              type="button"
              className={styles.signupLink}
              onClick={() => navigate('/onboarding')}
              disabled={isLoading}
            >
              Get Started
            </button>
          </div>
        </form>
      </div>

      {/* Footer */}
      <footer className={styles.loginFooter}>
        <p>© 2026 VIN Pulse Sales. All rights reserved.</p>
      </footer>
    </div>
  );
}
